import { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  MenuItem,
  Box,
  CircularProgress,
  Alert,
  Typography,
  Chip,
  Grid2 as Grid,
  Link,
  Divider,
  IconButton,
  Tooltip,
  FormControlLabel,
  Switch,
} from '@mui/material';
import {
  Schedule as ScheduleIcon,
  Close as CloseIcon,
  Search as SearchIcon,
  Visibility as VisibilityIcon,
  Edit as EditAccessIcon,
  Settings as SettingsIcon,
  Add as AddIcon,
  WhatsApp as WhatsAppIcon,
} from '@mui/icons-material';
import { formatTime } from '../utils/formatters';
import { maskPhoneBR } from '../utils/masks';
import DateField from './ui/DateField';
import api from '../services/api';
import personsService from '../services/personsService';
import servicesService from '../services/servicesService';
import whatsappService from '../services/whatsappService';
import appointmentsService from '../services/appointmentsService';
import ServicesManagerDialog from './services/ServicesManagerDialog';
import { BookingQuestions } from '../pages/customFields/CustomFieldsView';
import { useBookingQuestions } from '../pages/customFields/useBookingQuestions';

// Converte ISO UTC → valor para <input type="datetime-local"> (horário LOCAL do navegador)
const isoToLocalInput = (iso) => {
  if (!iso) return '';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '';
  const pad = (n) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
};

// Converte valor local do <input datetime-local> → ISO UTC (8601) para enviar ao backend
const localInputToIso = (local) => {
  if (!local) return null;
  const d = new Date(local);
  return Number.isNaN(d.getTime()) ? null : d.toISOString();
};

/**
 * AppointmentFormDialog - Modal auto-suficiente para criação de agendamentos
 * 
 * Carrega seus próprios dados via API ao abrir, independente da página onde está.
 * Pode ser usado em qualquer lugar sem necessidade de preparação prévia.
 * 
 * @param {Object} props
 * @param {boolean} props.open - Se o dialog está aberto
 * @param {Function} props.onClose - Callback ao fechar
 * @param {Function} props.onSuccess - Callback ao criar agendamento com sucesso
 * @param {Object} props.prefillData - Dados do cliente (nome, telefone, email)
 * @param {Object} props.initialData - Dados para reagendamento
 * @param {Date} props.preselectedDate - Data pré-selecionada
 * @param {Object} props.prefillConfig - Configuração de pré-preenchimento dinâmico
 * @param {string} props.prefillConfig.professionalId - ID do profissional a pré-selecionar
 * @param {string} props.prefillConfig.serviceId - ID do serviço a pré-selecionar
 * @param {string} props.prefillConfig.placeId - ID do local a pré-selecionar
 * @param {Array} props.prefillConfig.nextSlots - Próximos slots disponíveis (clicáveis)
 * @param {string} props.prefillConfig.slotStartTime - Horário já escolhido (ISO, um dos próximos livres):
 *   a janela abre com ele selecionado — o "agendar num toque" da tela de Profissionais
 */
export default function AppointmentFormDialog({
  open,
  onClose,
  onSuccess,
  prefillData = null,
  initialData = null,
  preselectedDate = null,
  prefillConfig = null,
  appointmentToEdit = null,
}) {
  const isEdit = !!appointmentToEdit;
  const [loading, setLoading] = useState(false);
  const [loadingData, setLoadingData] = useState(false);
  const [error, setError] = useState(null);
  const [dataLoaded, setDataLoaded] = useState(false);
  const [servicesManagerOpen, setServicesManagerOpen] = useState(false);
  
  // Dados carregados via API (auto-suficiente)
  const [professionals, setProfessionals] = useState([]);
  const [services, setServices] = useState([]);
  const [places, setPlaces] = useState([]);
  
  // Form state
  const [formData, setFormData] = useState({
    professionalId: '',
    serviceId: '',
    placeId: '',
    date: preselectedDate 
      ? preselectedDate.toISOString().split('T')[0] 
      : new Date().toISOString().split('T')[0],
    selectedSlot: null,
    clientName: initialData?.clientName || prefillData?.clientName || '',
    clientPhone: initialData?.clientPhone || prefillData?.clientPhone || '',
    clientEmail: initialData?.clientEmail || prefillData?.clientEmail || '',
    clientNotes: initialData?.clientNotes || '',
    // Item 5: notificar o cliente por WhatsApp na criação. Default ajustado ao abrir
    // conforme a config do dono (notifyOnPrivateCreate). O admin pode forçar/suprimir caso a caso.
    sendWhatsAppOnCreate: true,
    // Horário personalizado (encaixe/sobreposição — fluxo privado). Em edição, sempre ligado.
    customTime: false,
    customStart: '',
    customEnd: '',
  });

  // Sobreposições detectadas para o intervalo personalizado (apenas AVISO — não bloqueia)
  const [overlaps, setOverlaps] = useState([]);
  const [loadingOverlaps, setLoadingOverlaps] = useState(false);

  // Settings de WhatsApp do dono (para exibir/pré-marcar o checkbox de notificação na criação)
  const [waSettings, setWaSettings] = useState(null);
  // Confirmação antes de salvar quando um WhatsApp será disparado (mensagem tem custo —
  // o telefone precisa estar certo). Guarda o personId pendente do fluxo de sugestão.
  const [waConfirm, setWaConfirm] = useState({ open: false, personId: null });
  const navigate = useNavigate();

  // Próximos slots disponíveis (buscados automaticamente quando prof+service+place selecionados)
  const [nextAvailableSlots, setNextAvailableSlots] = useState([]);
  const [loadingNextSlots, setLoadingNextSlots] = useState(false);
  const slotDoPrefillAplicado = useRef(false); // prefillConfig.slotStartTime: seleciona uma vez por abertura
  
  // Available slots do dia selecionado
  const [availableSlots, setAvailableSlots] = useState([]);
  const [loadingSlots, setLoadingSlots] = useState(false);
  
  // Filtered data
  const [filteredServices, setFilteredServices] = useState([]);
  const [filteredPlaces, setFilteredPlaces] = useState([]);
  const [filteredProfessionals, setFilteredProfessionals] = useState([]);

  // Person suggestions state
  const [personSuggestions, setPersonSuggestions] = useState([]);
  const [loadingSuggestions, setLoadingSuggestions] = useState(false);
  const [selectedPerson, setSelectedPerson] = useState(null);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [confirmDialog, setConfirmDialog] = useState({ open: false, matches: [] });

  // Perguntas do agendamento: só na criação. Na edição, as respostas ficam no detalhe do agendamento.
  const bookingQuestions = useBookingQuestions({
    professionalId: isEdit ? null : formData.professionalId,
    placeId: formData.placeId,
    serviceId: formData.serviceId,
    personId: selectedPerson?.id,
  });
  const resetBookingQuestions = bookingQuestions.reset;

  // Verificar se há dados suficientes para agendar
  const hasNoData = useMemo(() => {
    return professionals.length === 0 || places.length === 0;
  }, [professionals, places]);

  // Verificar se um serviço específico tem profissionais que o atendem
  const serviceHasAvailableProfessionals = useCallback((serviceId) => {
    return places.some(place => 
      (place.professionals || []).some(pp => 
        pp.serviceIds?.includes(serviceId)
      )
    );
  }, [places]);

  // Carregar dados via API e aplicar pré-preenchimento quando o dialog abre
  useEffect(() => {
    if (!open) return;
    
    console.log('[AppointmentFormDialog] Dialog aberto com prefillConfig:', prefillConfig);
    
    // Reset estado
    setDataLoaded(false);
    setError(null);
    setNextAvailableSlots([]);
    setLoadingNextSlots(false);
    slotDoPrefillAplicado.current = false;
    resetBookingQuestions();

    // WhatsApp: saber se a integração está ativa e pré-marcar o checkbox conforme a config do dono.
    whatsappService.getSettings()
      .then((s) => {
        setWaSettings(s);
        setFormData((prev) => ({ ...prev, sendWhatsAppOnCreate: s?.notifyOnPrivateCreate ?? true }));
      })
      .catch(() => setWaSettings(null));

    // Carregar dados via API
    const loadFormData = async () => {
      try {
        setLoadingData(true);
        console.log('[AppointmentFormDialog] Carregando dados do formulário via API...');
        
        const response = await api.get('/appointments/form-data');
        const data = response.data || {};
        
        setProfessionals(data.professionals || []);
        setServices(data.services || []);
        setPlaces(data.places || []);
        
        console.log('[AppointmentFormDialog] Dados carregados:', {
          professionals: data.professionals?.length,
          services: data.services?.length,
          places: data.places?.length
        });
        
        // Aplicar pré-preenchimento após dados carregarem
        const newFormData = {
          professionalId: '',
          serviceId: '',
          placeId: '',
          date: preselectedDate 
            ? preselectedDate.toISOString().split('T')[0] 
            : new Date().toISOString().split('T')[0],
          selectedSlot: null,
          clientName: initialData?.clientName || prefillData?.clientName || '',
          clientPhone: initialData?.clientPhone || prefillData?.clientPhone || '',
          clientEmail: initialData?.clientEmail || prefillData?.clientEmail || '',
          clientNotes: initialData?.clientNotes || '',
          customTime: false,
          customStart: '',
          customEnd: '',
        };

        if (prefillConfig) {
          // Aplicar dados pré-preenchidos validando se existem
          if (prefillConfig.professionalId) {
            const profExists = (data.professionals || []).find(p => p.id === prefillConfig.professionalId);
            if (profExists) {
              newFormData.professionalId = prefillConfig.professionalId;
            } else {
              console.warn('[AppointmentFormDialog] Profissional não encontrado:', prefillConfig.professionalId);
            }
          }
          
          if (prefillConfig.placeId) {
            const placeExists = (data.places || []).find(p => p.id === prefillConfig.placeId);
            if (placeExists) {
              newFormData.placeId = prefillConfig.placeId;
            } else {
              console.warn('[AppointmentFormDialog] Local não encontrado:', prefillConfig.placeId);
            }
          }
          
          if (prefillConfig.serviceId) {
            const serviceExists = (data.services || []).find(s => s.id === prefillConfig.serviceId);
            if (serviceExists) {
              // Verificar se o serviço tem profissionais disponíveis
              const hasProfs = (data.places || []).some(place => 
                (place.professionals || []).some(pp => 
                  pp.serviceIds?.includes(prefillConfig.serviceId)
                )
              );
              if (!hasProfs) {
                setError(`O serviço "${serviceExists.name}" não tem profissionais disponíveis em nenhum local. Configure um local com profissionais que atendam este serviço.`);
              } else {
                newFormData.serviceId = prefillConfig.serviceId;
              }
            } else {
              console.warn('[AppointmentFormDialog] Serviço não encontrado:', prefillConfig.serviceId);
            }
          }
        }

        // Modo edição: pré-carregar prof/serviço/local/cliente + horário personalizado (encaixe).
        if (appointmentToEdit) {
          newFormData.professionalId = appointmentToEdit.professional?.id
            || appointmentToEdit.professionalId || '';
          newFormData.serviceId = appointmentToEdit.service?.id
            || appointmentToEdit.serviceId || '';
          newFormData.placeId = appointmentToEdit.place?.id
            || appointmentToEdit.placeId || '';
          newFormData.clientName = appointmentToEdit.clientName || '';
          newFormData.clientPhone = appointmentToEdit.clientPhone
            ? maskPhoneBR(appointmentToEdit.clientPhone) : '';
          newFormData.clientEmail = appointmentToEdit.clientEmail || '';
          newFormData.clientNotes = appointmentToEdit.clientNotes || '';
          newFormData.customTime = true;
          newFormData.customStart = isoToLocalInput(appointmentToEdit.startTime);
          newFormData.customEnd = isoToLocalInput(appointmentToEdit.endTime);
        }

        setFormData(newFormData);
        setDataLoaded(true);
      } catch (err) {
        console.error('[AppointmentFormDialog] Erro ao carregar dados:', err);
        setError('Erro ao carregar dados do formulário. Tente novamente.');
      } finally {
        setLoadingData(false);
      }
    };
    
    loadFormData();
  }, [open, prefillConfig, preselectedDate, initialData, prefillData, appointmentToEdit, resetBookingQuestions]);

  // Filtrar serviços baseado no profissional selecionado
  useEffect(() => {
    if (!formData.professionalId) {
      setFilteredServices([]);
      return;
    }
    
    // Buscar todos os places onde o profissional atende
    const professionalLinks = places.flatMap(place => 
      (place.professionals || []).filter(pp => pp.professionalId === formData.professionalId)
    );

    // Extrair serviceIds únicos dos vínculos
    const serviceIds = new Set();
    professionalLinks.forEach(pp => {
      if (pp.serviceIds && Array.isArray(pp.serviceIds)) {
        pp.serviceIds.forEach(id => serviceIds.add(id));
      }
    });

    // Filtrar serviços
    const filtered = services.filter(s => serviceIds.has(s.id));
    setFilteredServices(filtered);
  }, [formData.professionalId, places, services]);

  // Não precisamos mais filtrar profissionais (sempre mostra todos)
  useEffect(() => {
    setFilteredProfessionals(professionals);
  }, [professionals]);

  // Filtrar locais baseado no profissional e serviço
  useEffect(() => {
    if (!formData.professionalId || !formData.serviceId) {
      setFilteredPlaces([]);
      return;
    }
    
    // Buscar places onde o profissional atende E oferece o serviço selecionado
    const validPlaces = places.filter(place => {
      const professionalLink = (place.professionals || []).find(
        pp => pp.professionalId === formData.professionalId &&
              pp.serviceIds?.includes(formData.serviceId)
      );
      return professionalLink !== undefined;
    });

    setFilteredPlaces(validPlaces);
  }, [formData.professionalId, formData.serviceId, places]);

  // Atualizar data quando preselectedDate mudar
  useEffect(() => {
    if (preselectedDate) {
      setFormData(prev => ({
        ...prev,
        date: preselectedDate.toISOString().split('T')[0]
      }));
    }
  }, [preselectedDate]);

  // Buscar próximos slots disponíveis quando profissional + serviço + local estiverem selecionados
  useEffect(() => {
    if (!formData.professionalId || !formData.serviceId || !formData.placeId) {
      setNextAvailableSlots([]);
      return;
    }

    const loadNextSlots = async () => {
      try {
        setLoadingNextSlots(true);
        const response = await api.get('/appointments/next-available-slots', {
          params: {
            professionalId: formData.professionalId,
            serviceId: formData.serviceId,
            placeId: formData.placeId,
            count: 5
          }
        });
        setNextAvailableSlots(response.data || []);
        // Veio de "agendar neste horário" (tela de Profissionais): abre com o horário já escolhido,
        // pelo mesmo caminho do clique num dos próximos horários desta janela.
        if (prefillConfig?.slotStartTime && !slotDoPrefillAplicado.current) {
          slotDoPrefillAplicado.current = true;
          handleNextSlotClick({ startTime: prefillConfig.slotStartTime });
        }
      } catch (err) {
        console.error('Erro ao buscar próximos horários:', err);
        setNextAvailableSlots([]);
      } finally {
        setLoadingNextSlots(false);
      }
    };

    loadNextSlots();
    // Só a trinca profissional × serviço × local recarrega os próximos horários; o horário do prefill é
    // aplicado uma vez por abertura (ref) e handleNextSlotClick muda a cada render.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [formData.professionalId, formData.serviceId, formData.placeId]);

  // Parâmetro slotToSelect: slot para selecionar automaticamente após carregar (passado diretamente para evitar problemas de estado assíncrono)
  const loadAvailableSlots = async (slotToSelect = null) => {
    try {
      setLoadingSlots(true);
      setError(null);

      const response = await api.get('/appointments/available-slots', {
        params: {
          professionalId: formData.professionalId,
          serviceId: formData.serviceId,
          placeId: formData.placeId,
          date: formData.date
        }
      });

      const slots = response.data || [];
      setAvailableSlots(slots);
      
      // Se foi passado um slot para selecionar, buscar e selecionar
      if (slotToSelect) {
        const matchingSlot = slots.find(
          s => new Date(s.startTime).getTime() === new Date(slotToSelect.startTime).getTime()
        );
        if (matchingSlot) {
          setFormData(prev => ({ ...prev, selectedSlot: matchingSlot }));
        }
      } else if (formData.selectedSlot) {
        // Reset selected slot se não estiver mais disponível
        const slotStillAvailable = slots.some(
          s => new Date(s.startTime).getTime() === new Date(formData.selectedSlot.startTime).getTime()
        );
        if (!slotStillAvailable) {
          setFormData(prev => ({ ...prev, selectedSlot: null }));
        }
      }
    } catch (err) {
      console.error('Erro ao carregar horários disponíveis:', err);
      setError('Erro ao carregar horários disponíveis');
      setAvailableSlots([]);
    } finally {
      setLoadingSlots(false);
    }
  };

  const handleChange = (field, value) => {
    // Reset campos dependentes de forma simples (profissional → serviço → local)
    if (field === 'professionalId') {
      setFormData(prev => ({
        ...prev,
        professionalId: value,
        serviceId: '',
        placeId: '',
        selectedSlot: null
      }));
      setAvailableSlots([]);
    } else if (field === 'serviceId') {
      setFormData(prev => ({
        ...prev,
        serviceId: value,
        placeId: '',
        selectedSlot: null
      }));
      setAvailableSlots([]);
    } else if (field === 'placeId' || field === 'date') {
      setFormData(prev => ({ ...prev, [field]: value, selectedSlot: null }));
      setAvailableSlots([]);
    } else {
      setFormData(prev => ({ ...prev, [field]: value }));
    }
  };

  // Handler para fechar modal de gerenciamento de serviços e recarregar
  const handleServicesManagerClose = async () => {
    setServicesManagerOpen(false);
    try {
      const freshServices = await servicesService.getAll();
      setServices(freshServices || []);
    } catch (err) {
      console.warn('Erro ao recarregar serviços:', err);
    }
  };

  // Handler para clicar em um slot dos próximos disponíveis
  // Define a data e carrega horários do dia, passando o slot para seleção automática
  const handleNextSlotClick = (slot) => {
    const slotDate = new Date(slot.startTime);
    const newDate = slotDate.toISOString().split('T')[0];
    
    // Atualizar data primeiro
    setFormData(prev => ({
      ...prev,
      date: newDate,
      selectedSlot: null
    }));
    
    // Buscar horários do dia passando o slot diretamente para seleção
    // Usar setTimeout para garantir que o state foi atualizado
    setTimeout(async () => {
      try {
        setLoadingSlots(true);
        setError(null);

        const response = await api.get('/appointments/available-slots', {
          params: {
            professionalId: formData.professionalId,
            serviceId: formData.serviceId,
            placeId: formData.placeId,
            date: newDate // Usar a data diretamente, não do state
          }
        });

        const slots = response.data || [];
        setAvailableSlots(slots);
        
        // Buscar o slot correspondente e selecionar
        const matchingSlot = slots.find(
          s => new Date(s.startTime).getTime() === new Date(slot.startTime).getTime()
        );
        if (matchingSlot) {
          setFormData(prev => ({ ...prev, selectedSlot: matchingSlot }));
        }
      } catch (err) {
        console.error('Erro ao carregar horários disponíveis:', err);
        setError('Erro ao carregar horários disponíveis');
        setAvailableSlots([]);
      } finally {
        setLoadingSlots(false);
      }
    }, 50);
  };

  // === Person suggestion logic ===

  const fetchPersonSuggestions = useCallback(async (phone, email) => {
    const normalizedPhone = (phone || '').replace(/\D/g, '');

    if (normalizedPhone.length < 10 && (!email || !email.includes('@'))) {
      setPersonSuggestions([]);
      setShowSuggestions(false);
      return;
    }

    try {
      setLoadingSuggestions(true);
      const params = {};
      if (normalizedPhone.length >= 10) params.phone = normalizedPhone;
      if (email && email.includes('@')) params.email = email;

      const suggestions = await personsService.getSuggestions(params);
      setPersonSuggestions(suggestions || []);
      setShowSuggestions((suggestions || []).length > 0);
    } catch (err) {
      console.error('Erro ao buscar sugestões de pessoas:', err);
      setPersonSuggestions([]);
      setShowSuggestions(false);
    } finally {
      setLoadingSuggestions(false);
    }
  }, []);

  const handleSelectPerson = (person) => {
    setSelectedPerson(person);
    setShowSuggestions(false);
    setFormData(prev => ({
      ...prev,
      clientName: person.name || prev.clientName,
      clientPhone: person.phone ? maskPhoneBR(person.phone) : prev.clientPhone,
      clientEmail: person.email || prev.clientEmail,
    }));
  };

  const handleDeselectPerson = () => {
    setSelectedPerson(null);
    setShowSuggestions(false);
  };

  // ── Horário personalizado (encaixe/sobreposição) ───────────────────────────
  const customStartDate = formData.customStart ? new Date(formData.customStart) : null;
  const customEndDate = formData.customEnd ? new Date(formData.customEnd) : null;
  const customReady = formData.customTime
    && customStartDate && customEndDate
    && !Number.isNaN(customStartDate.getTime()) && !Number.isNaN(customEndDate.getTime())
    && customEndDate > customStartDate;

  // ScheduleId para o encaixe: na edição vem do próprio agendamento; na criação,
  // reusa o scheduleId dos slots já carregados (não depende de chamada extra).
  const resolvedScheduleId = isEdit
    ? appointmentToEdit?.scheduleId
    : (nextAvailableSlots[0]?.scheduleId || availableSlots[0]?.scheduleId || null);

  const handleToggleCustomTime = (checked) => {
    setOverlaps([]);
    setFormData(prev => {
      if (!checked) return { ...prev, customTime: false };
      let start = prev.customStart;
      if (!start) {
        const base = prev.selectedSlot?.startTime
          ? new Date(prev.selectedSlot.startTime)
          : new Date(`${prev.date}T09:00`);
        start = isoToLocalInput(base.toISOString());
      }
      let end = prev.customEnd;
      if (!end || new Date(end) <= new Date(start)) {
        const svc = services.find(s => s.id === prev.serviceId);
        const dur = svc?.defaultDurationMinutes || 30;
        end = isoToLocalInput(new Date(new Date(start).getTime() + dur * 60000).toISOString());
      }
      return { ...prev, customTime: true, customStart: start, customEnd: end };
    });
  };

  const handleCustomStartChange = (val) => {
    setFormData(prev => {
      let nextEnd = prev.customEnd;
      if (!nextEnd || new Date(nextEnd) <= new Date(val)) {
        const svc = services.find(s => s.id === prev.serviceId);
        const dur = svc?.defaultDurationMinutes || 30;
        nextEnd = isoToLocalInput(new Date(new Date(val).getTime() + dur * 60000).toISOString());
      }
      return { ...prev, customStart: val, customEnd: nextEnd };
    });
  };

  // Busca sobreposições (apenas AVISO) quando o intervalo personalizado fica válido
  useEffect(() => {
    if (!formData.customTime || !formData.professionalId || !customReady) {
      setOverlaps([]);
      return undefined;
    }
    let cancelled = false;
    const t = setTimeout(async () => {
      try {
        setLoadingOverlaps(true);
        const result = await appointmentsService.getOverlaps({
          professionalId: formData.professionalId,
          startTime: localInputToIso(formData.customStart),
          endTime: localInputToIso(formData.customEnd),
          excludeAppointmentId: appointmentToEdit?.id,
        });
        if (!cancelled) setOverlaps(result || []);
      } catch (err) {
        console.error('Erro ao verificar sobreposições:', err);
        if (!cancelled) setOverlaps([]);
      } finally {
        if (!cancelled) setLoadingOverlaps(false);
      }
    }, 400);
    return () => { cancelled = true; clearTimeout(t); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [formData.customTime, formData.professionalId, formData.customStart, formData.customEnd]);

  const doSubmit = async (personId = null, { waConfirmed = false } = {}) => {
    // Vai disparar WhatsApp na criação? Confirmar o telefone antes (mensagem tem custo).
    if (!waConfirmed && !isEdit && waSettings?.enabled && formData.sendWhatsAppOnCreate) {
      setWaConfirm({ open: true, personId });
      return;
    }
    try {
      setLoading(true);
      setError(null);

      // Edição: PUT com horário personalizado (encaixe/sobreposição admin)
      if (isEdit) {
        await appointmentsService.update(appointmentToEdit.id, {
          clientName: formData.clientName,
          clientPhone: formData.clientPhone,
          clientEmail: formData.clientEmail || null,
          clientNotes: formData.clientNotes || null,
          serviceId: formData.serviceId || null,
          startTime: localInputToIso(formData.customStart),
          endTime: localInputToIso(formData.customEnd),
        });
        onSuccess();
        return;
      }

      // Criação com horário personalizado (fora da grade / sobreposição)
      if (formData.customTime) {
        if (!resolvedScheduleId) {
          setError('Não foi possível resolver a agenda do profissional para o encaixe. Selecione um profissional/serviço/local com agenda configurada.');
          return;
        }
        await api.post('/appointments', {
          professionalId: formData.professionalId,
          placeId: formData.placeId,
          scheduleId: resolvedScheduleId,
          serviceId: formData.serviceId,
          startTime: localInputToIso(formData.customStart),
          endTime: localInputToIso(formData.customEnd),
          clientName: formData.clientName,
          clientPhone: formData.clientPhone,
          clientEmail: formData.clientEmail || null,
          clientNotes: formData.clientNotes || null,
          personId: personId || selectedPerson?.id || null,
          sendWhatsAppOnCreate: waSettings?.enabled ? formData.sendWhatsAppOnCreate : null,
          customFieldAnswers: bookingQuestions.answers(),
        });
        onSuccess();
        return;
      }

      // Criação normal (slot da grade)
      await api.post('/appointments', {
        professionalId: formData.professionalId,
        placeId: formData.placeId,
        scheduleId: formData.selectedSlot.scheduleId,
        serviceId: formData.serviceId,
        startTime: formData.selectedSlot.startTime,
        clientName: formData.clientName,
        clientPhone: formData.clientPhone,
        clientEmail: formData.clientEmail || null,
        clientNotes: formData.clientNotes || null,
        personId: personId || selectedPerson?.id || null,
        // Item 5: override por agendamento de "notificar na criação" (só relevante se WhatsApp ativo).
        sendWhatsAppOnCreate: waSettings?.enabled ? formData.sendWhatsAppOnCreate : null,
        customFieldAnswers: bookingQuestions.answers(),
      });

      onSuccess();
    } catch (err) {
      console.error('Erro ao salvar agendamento:', err);
      setError(err.response?.data?.message || 'Erro ao salvar agendamento');
      bookingQuestions.showErrors(err);
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async () => {
    // Horário: modo personalizado/edição exige início&lt;fim válidos; modo grade exige slot.
    if (formData.customTime || isEdit) {
      if (!customReady) {
        setError('Informe início e fim válidos (o fim deve ser depois do início).');
        return;
      }
    } else if (!formData.selectedSlot) {
      setError('Selecione um horário disponível');
      return;
    }

    if (!formData.clientName.trim()) {
      setError('Nome do cliente é obrigatório');
      return;
    }

    if (!formData.clientPhone.trim()) {
      setError('Telefone do cliente é obrigatório');
      return;
    }

    const phoneDigits = formData.clientPhone.replace(/\D/g, '');
    if (phoneDigits.length < 10 || phoneDigits.length > 11) {
      setError('Telefone inválido. Informe um número com DDD (10 dígitos para fixo ou 11 para celular).');
      return;
    }

    // Sugestão de pessoa só na criação (na edição o cliente já está vinculado)
    if (!isEdit && !selectedPerson && personSuggestions.length > 0) {
      setConfirmDialog({ open: true, matches: personSuggestions });
      return;
    }

    await doSubmit();
  };

  const handleClose = () => {
    if (!loading && !loadingData) {
      setFormData({
        professionalId: '',
        serviceId: '',
        placeId: '',
        date: new Date().toISOString().split('T')[0],
        selectedSlot: null,
        clientName: '',
        clientPhone: '',
        clientEmail: '',
        clientNotes: '',
        sendWhatsAppOnCreate: true,
        customTime: false,
        customStart: '',
        customEnd: '',
      });
      // Limpar dados carregados via API
      setProfessionals([]);
      setServices([]);
      setPlaces([]);
      setAvailableSlots([]);
      setNextAvailableSlots([]);
      setOverlaps([]);
      setFilteredServices([]);
      setFilteredPlaces([]);
      setFilteredProfessionals([]);
      setPersonSuggestions([]);
      setSelectedPerson(null);
      setShowSuggestions(false);
      setConfirmDialog({ open: false, matches: [] });
      setWaConfirm({ open: false, personId: null });
      setError(null);
      setDataLoaded(false);
      onClose();
    }
  };

  const formatSlotTime = (slot) => {
    return formatTime(slot.startTime);
  };

  const formatSlotDateTime = (slot) => {
    const date = new Date(slot.startTime);
    return date.toLocaleDateString('pt-BR', { 
      weekday: 'short', 
      day: '2-digit', 
      month: '2-digit',
      hour: '2-digit', 
      minute: '2-digit' 
    });
  };

  // Verifica se o profissional selecionado permite criar agendamentos
  // Owner tem acesso total; compartilhado precisa de ReadWrite (2) em Appointments (entityType=2)
  const canScheduleProfessional = (prof) => {
    if (!prof) return true;
    if (!prof.isShared) return true;
    return (prof.permissions?.[2] ?? 0) >= 2;
  };

  const selectedProfessional = professionals.find(p => p.id === formData.professionalId);
  const selectedProfCanSchedule = canScheduleProfessional(selectedProfessional);
  // Horário satisfeito: modo personalizado/edição usa customReady; modo grade usa selectedSlot.
  const hasTimeSelected = (formData.customTime || isEdit) ? customReady : !!formData.selectedSlot;
  const canSubmit = hasTimeSelected && formData.clientName.trim() && formData.clientPhone.trim() && !loading && selectedProfCanSchedule;

  // Determinar motivo da desabilitação do botão para feedback ao usuário
  const getSubmitButtonDisabledReason = () => {
    if (!hasTimeSelected) {
      return (formData.customTime || isEdit)
        ? 'Informe início e fim válidos (fim depois do início)'
        : 'Selecione um horário disponível';
    }
    if (!formData.clientName.trim()) return 'Preencha o nome do cliente';
    if (!formData.clientPhone.trim()) return 'Preencha o telefone do cliente';
    if (!selectedProfCanSchedule) return 'Sem permissão para agendar com este profissional';
    if (loading) return 'Processando...';
    return null;
  };

  const submitButtonDisabledReason = getSubmitButtonDisabledReason();

  // Sempre mostrar todos os campos - prefillConfig apenas pré-seleciona valores
  // O usuário pode alterar qualquer seleção se desejar

  // Info de pré-preenchimento removida - agora usamos apenas dropdowns editáveis

  // Renderizar campos de seleção - sempre na mesma ordem: profissional → serviço → local
  // Prefill apenas pré-seleciona valores, todos os campos são editáveis
  const renderSelectionFields = () => {
    return (
      <>
        {/* Step 1: Professional */}
        <TextField
          select
          fullWidth
          label="Profissional"
          value={formData.professionalId}
          onChange={(e) => handleChange('professionalId', e.target.value)}
          disabled={loading}
          sx={{ mb: 2 }}
        >
          <MenuItem value=""><em>Selecione um profissional</em></MenuItem>
          {professionals.map((prof) => {
            const canSchedule = canScheduleProfessional(prof);
            const showBadge = prof.isShared;
            const hasWrite = (prof.permissions?.[2] ?? 0) >= 2;
            return (
              <MenuItem key={prof.id} value={prof.id} disabled={!canSchedule}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, width: '100%' }}>
                  <span>{prof.name}{prof.specialty && ` - ${prof.specialty}`}</span>
                  {showBadge && (
                    <Box component="span" sx={{ display: 'inline-flex', alignItems: 'center', ml: 'auto', pl: 1 }}>
                      {hasWrite
                        ? <EditAccessIcon sx={{ fontSize: 15, color: 'success.main' }} titleAccess="Permissão de edição" />
                        : <VisibilityIcon sx={{ fontSize: 15, color: 'text.secondary' }} titleAccess="Somente visualização" />}
                    </Box>
                  )}
                </Box>
              </MenuItem>
            );
          })}
        </TextField>

        {/* Aviso quando profissional compartilhado não tem permissão de agendamento */}
        {formData.professionalId && !selectedProfCanSchedule && (
          <Alert severity="warning" sx={{ mb: 2 }}>
            Você tem apenas acesso de <strong>Visualização</strong> nos agendamentos deste profissional. Para criar agendamentos é necessária permissão de <strong>Edição</strong>.
          </Alert>
        )}

        {/* Step 2: Service (filtrado pelo profissional) */}
        <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1, mb: 2 }}>
          <TextField
            select
            fullWidth
            label="Serviço"
            value={formData.serviceId}
            onChange={(e) => {
              if (e.target.value === '__create_new__') {
                setServicesManagerOpen(true);
                return;
              }
              handleChange('serviceId', e.target.value);
            }}
            disabled={loading || !formData.professionalId}
            helperText={formData.professionalId && filteredServices.length === 0 ? 'Nenhum serviço disponível para este profissional' : ''}
          >
            <MenuItem value=""><em>Selecione um serviço</em></MenuItem>
            {filteredServices.map((service) => (
              <MenuItem key={service.id} value={service.id}>
                {service.name}
              </MenuItem>
            ))}
            {/* Separador */}
            {filteredServices.length > 0 && <Divider />}
            {/* Opção de criar novo */}
            <MenuItem value="__create_new__" sx={{ color: 'primary.main', fontWeight: 'bold' }}>
              <AddIcon sx={{ mr: 1, fontSize: '1.2rem' }} /> Criar Novo Serviço
            </MenuItem>
          </TextField>
          <Tooltip title="Gerenciar serviços">
            <IconButton
              onClick={() => setServicesManagerOpen(true)}
              sx={{ mt: 1 }}
              size="small"
            >
              <SettingsIcon />
            </IconButton>
          </Tooltip>
        </Box>

        {/* Step 3: Place (filtrado pelo profissional + serviço) */}
        <TextField
          select
          fullWidth
          label="Local"
          value={formData.placeId}
          onChange={(e) => handleChange('placeId', e.target.value)}
          disabled={loading || !formData.serviceId}
          helperText={formData.serviceId && filteredPlaces.length === 0 ? 'Este profissional não atende este serviço em nenhum local' : ''}
          sx={{ mb: 2 }}
        >
          <MenuItem value=""><em>Selecione um local</em></MenuItem>
          {filteredPlaces.map((place) => (
            <MenuItem key={place.id} value={place.id}>
              {place.name}
            </MenuItem>
          ))}
        </TextField>

        {/* Próximos horários disponíveis (aparece após selecionar prof + serviço + local) */}
        {!formData.customTime && formData.professionalId && formData.serviceId && formData.placeId && (
          <Box sx={{ mb: 2 }}>
            <Typography variant="subtitle2" gutterBottom color="primary">
              🚀 Próximos horários disponíveis (clique para selecionar):
            </Typography>
            {loadingNextSlots ? (
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, py: 1 }}>
                <CircularProgress size={16} />
                <Typography variant="body2" color="text.secondary">
                  Buscando próximos horários...
                </Typography>
              </Box>
            ) : nextAvailableSlots.length > 0 ? (
              <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                {nextAvailableSlots.map((slot, index) => (
                  <Chip
                    key={index}
                    label={formatSlotDateTime(slot)}
                    onClick={() => handleNextSlotClick(slot)}
                    color="default"
                    variant="outlined"
                    sx={{ cursor: 'pointer' }}
                  />
                ))}
              </Box>
            ) : (
              <Typography variant="body2" color="text.secondary">
                Nenhum horário disponível nos próximos dias.
              </Typography>
            )}
          </Box>
        )}
      </>
    );
  };

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="md" fullWidth>
      <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <ScheduleIcon color="primary" />
          {isEdit ? 'Editar Agendamento' : 'Novo Agendamento'}
        </Box>
        <IconButton onClick={handleClose} disabled={loading || loadingData} size="small">
          <CloseIcon />
        </IconButton>
      </DialogTitle>
      <DialogContent>
        {/* Loading state enquanto carrega dados */}
        {loadingData && (
          <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', py: 4 }}>
            <CircularProgress />
            <Typography sx={{ ml: 2 }} color="text.secondary">
              Carregando dados...
            </Typography>
          </Box>
        )}

        {/* Alerta se não há dados */}
        {!loadingData && hasNoData && (
          <Alert 
            severity="warning" 
            sx={{ mb: 2 }}
          >
            {professionals.length === 0 && places.length === 0 
              ? 'Você precisa criar um local com profissionais antes de agendar.'
              : professionals.length === 0 
                ? 'Nenhum profissional cadastrado.'
                : 'Nenhum local cadastrado.'
            }
          </Alert>
        )}

        {!loadingData && error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}

        {/* Conteúdo do formulário - só mostra após carregar dados */}
        {!loadingData && !hasNoData && (
          <>

        <Box sx={{ mt: 2 }}>
          {/* Campos de seleção - sempre na mesma ordem */}
          {!hasNoData && renderSelectionFields()}

          {/* Toggle: horário personalizado (encaixe / sobreposição) — fluxo privado */}
          {formData.professionalId && formData.serviceId && formData.placeId && (
            <Box sx={{ mb: 1 }}>
              <FormControlLabel
                control={
                  <Switch
                    checked={formData.customTime}
                    onChange={(e) => handleToggleCustomTime(e.target.checked)}
                    color="primary"
                    disabled={loading || isEdit}
                  />
                }
                label="Horário personalizado (encaixe / sobreposição)"
              />
              <Typography variant="caption" color="text.secondary" sx={{ display: 'block', ml: 0.5 }}>
                Define início e fim livres, fora da grade. Permite atender duas pessoas ao mesmo tempo.
              </Typography>
            </Box>
          )}

          {/* Horário personalizado: início/fim livres + aviso (não bloqueante) de sobreposição */}
          {formData.customTime && formData.serviceId && formData.placeId && (
            <Box sx={{ mb: 3 }}>
              <Box sx={{ display: 'flex', gap: 1, mb: 1, flexWrap: 'wrap' }}>
                <TextField
                  label="Início"
                  type="datetime-local"
                  value={formData.customStart}
                  onChange={(e) => handleCustomStartChange(e.target.value)}
                  disabled={loading}
                  InputLabelProps={{ shrink: true }}
                  sx={{ flex: 1, minWidth: 200 }}
                />
                <TextField
                  label="Fim"
                  type="datetime-local"
                  value={formData.customEnd}
                  onChange={(e) => handleChange('customEnd', e.target.value)}
                  disabled={loading}
                  InputLabelProps={{ shrink: true }}
                  sx={{ flex: 1, minWidth: 200 }}
                />
              </Box>
              {formData.customStart && formData.customEnd && !customReady && (
                <Typography variant="caption" color="error" sx={{ display: 'block', mb: 1 }}>
                  O fim deve ser depois do início.
                </Typography>
              )}
              {loadingOverlaps && (
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                  <CircularProgress size={14} />
                  <Typography variant="caption" color="text.secondary">Verificando sobreposições...</Typography>
                </Box>
              )}
              {overlaps.length > 0 && (
                <Alert severity="warning" sx={{ mb: 1 }}>
                  Este horário se sobrepõe a {overlaps.length} agendamento(s) do profissional:
                  <Box component="ul" sx={{ m: 0.5, pl: 2 }}>
                    {overlaps.map((o) => (
                      <li key={o.id}>
                        {o.clientName} · {formatTime(o.startTime)}–{formatTime(o.endTime)}
                      </li>
                    ))}
                  </Box>
                  Você pode prosseguir mesmo assim (sobreposição permitida).
                </Alert>
              )}
            </Box>
          )}

          {/* Date (modo grade) */}
          {!formData.customTime && formData.placeId && formData.serviceId && (
            <Box sx={{ display: 'flex', gap: 1, mb: 2 }}>
              <DateField
                label="Data"
                value={formData.date}
                onChange={(val) => handleChange('date', val)}
                disabled={loading}
              />
              <Button
                variant="contained"
                onClick={loadAvailableSlots}
                disabled={loading || loadingSlots || !formData.date}
                startIcon={loadingSlots ? <CircularProgress size={16} /> : <SearchIcon />}
                sx={{ minWidth: '140px', whiteSpace: 'nowrap' }}
              >
                {loadingSlots ? 'Buscando...' : 'Buscar Horários'}
              </Button>
            </Box>
          )}

          {/* Available Slots (modo grade) */}
          {!formData.customTime && formData.date && formData.serviceId && formData.placeId && (
            <Box sx={{ mb: 3 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mb: 1 }}>
                <Typography variant="subtitle2">
                  Horários Disponíveis
                </Typography>
                <Typography variant="caption" color="error" sx={{ fontWeight: 'bold' }}>
                  *
                </Typography>
              </Box>
              {loadingSlots ? (
                <Box display="flex" justifyContent="center" p={2}>
                  <CircularProgress size={24} />
                </Box>
              ) : availableSlots.length === 0 ? (
                <Alert severity="info">
                  {availableSlots === null ? 'Clique em "Buscar Horários" para ver disponibilidade' : 'Nenhum horário disponível para esta data'}
                </Alert>
              ) : (
                <>
                  <Grid container spacing={1} sx={{ mb: 1 }}>
                    {availableSlots.map((slot, index) => (
                      <Grid size={{ xs: 4, sm: 3, md: 2 }} key={index}>
                        <Chip
                          label={formatSlotTime(slot)}
                          onClick={() => setFormData(prev => ({ ...prev, selectedSlot: slot }))}
                          color={
                            formData.selectedSlot?.startTime === slot.startTime
                              ? 'primary'
                              : 'default'
                          }
                          variant={
                            formData.selectedSlot?.startTime === slot.startTime
                              ? 'filled'
                              : 'outlined'
                          }
                          sx={{ width: '100%', cursor: 'pointer', fontWeight: formData.selectedSlot?.startTime === slot.startTime ? 'bold' : 'normal' }}
                        />
                      </Grid>
                    ))}
                  </Grid>
                  {!formData.selectedSlot && (
                    <Typography variant="caption" color="warning.main" sx={{ display: 'block', mb: 1 }}>
                      ⚠️ Selecione um horário para continuar
                    </Typography>
                  )}
                </>
              )}
            </Box>
          )}

          {/* Client Info */}
          {(formData.selectedSlot || (formData.customTime && customReady)) && (
            <>
              <Divider sx={{ my: 2 }} />
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mb: 2 }}>
                <Typography variant="subtitle2">
                  Dados do Cliente
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  (campos obrigatórios indicados com *)
                </Typography>
              </Box>
              
              <TextField
                fullWidth
                label="Nome do Cliente"
                value={formData.clientName}
                onChange={(e) => handleChange('clientName', e.target.value)}
                disabled={loading}
                required
                placeholder="Digite o nome completo"
                sx={{ mb: 2 }}
              />

              <TextField
                fullWidth
                label="Telefone"
                placeholder="(00) 00000-0000"
                value={formData.clientPhone}
                required
                onChange={(e) => {
                  handleChange('clientPhone', maskPhoneBR(e.target.value));
                  // Limpar seleção ao digitar manualmente
                  if (selectedPerson) setSelectedPerson(null);
                }}
                onBlur={() => {
                  if (!selectedPerson) fetchPersonSuggestions(formData.clientPhone, formData.clientEmail);
                }}
                disabled={loading}
                inputProps={{ maxLength: 15 }}
                helperText="DDD obrigatório (10 ou 11 dígitos)"
                sx={{ mb: 2 }}
              />

              <TextField
                fullWidth
                label="E-mail"
                type="email"
                value={formData.clientEmail}
                onChange={(e) => {
                  handleChange('clientEmail', e.target.value);
                  if (selectedPerson) setSelectedPerson(null);
                }}
                onBlur={() => {
                  if (!selectedPerson) fetchPersonSuggestions(formData.clientPhone, formData.clientEmail);
                }}
                disabled={loading}
                sx={{ mb: 2 }}
              />

              {/* Pessoa selecionada */}
              {selectedPerson && (
                <Box sx={{ mb: 2, p: 1.5, bgcolor: '#e8f5e9', borderRadius: 1, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <Typography variant="body2">
                    Pessoa selecionada: <strong>{selectedPerson.name}</strong>
                    {selectedPerson.phone && ` · ${maskPhoneBR(selectedPerson.phone)}`}
                  </Typography>
                  <IconButton size="small" onClick={handleDeselectPerson} title="Desfazer seleção">
                    <CloseIcon fontSize="small" />
                  </IconButton>
                </Box>
              )}

              {/* Sugestões de pessoas */}
              {showSuggestions && personSuggestions.length > 0 && !selectedPerson && (
                <Box sx={{ mb: 2, p: 1.5, bgcolor: '#e3f2fd', borderRadius: 1, border: '1px solid #90caf9' }}>
                  <Typography variant="caption" color="text.secondary" sx={{ mb: 1, display: 'block' }}>
                    Pessoas encontradas com dados semelhantes:
                  </Typography>
                  {personSuggestions.map(person => (
                    <Box
                      key={person.id}
                      onClick={() => handleSelectPerson(person)}
                      sx={{
                        p: 1, mb: 0.5, bgcolor: 'background.paper', borderRadius: 1,
                        cursor: 'pointer', '&:hover': { bgcolor: '#f5f5f5' },
                        display: 'flex', justifyContent: 'space-between', alignItems: 'center'
                      }}
                    >
                      <Box>
                        <Typography variant="body2" fontWeight="bold">{person.name}</Typography>
                        <Typography variant="caption" color="text.secondary">
                          {person.phone ? maskPhoneBR(person.phone) : ''}{person.email ? ` · ${person.email}` : ''}
                        </Typography>
                      </Box>
                      <Chip label="Usar" size="small" color="primary" variant="outlined" />
                    </Box>
                  ))}
                </Box>
              )}

              {loadingSuggestions && (
                <Box sx={{ mb: 2, display: 'flex', alignItems: 'center', gap: 1 }}>
                  <CircularProgress size={16} />
                  <Typography variant="caption" color="text.secondary">Buscando pessoas...</Typography>
                </Box>
              )}

              <TextField
                fullWidth
                label="Observações"
                multiline
                rows={3}
                value={formData.clientNotes}
                onChange={(e) => handleChange('clientNotes', e.target.value)}
                disabled={loading}
              />

              {!isEdit && (
                <Box sx={{ mt: 2.5 }}>
                  <BookingQuestions vm={bookingQuestions.vm} actions={bookingQuestions.actions} />
                </Box>
              )}

              <Box sx={{ mt: 1 }}>
                <FormControlLabel
                  control={
                    <Switch
                      checked={waSettings?.enabled ? formData.sendWhatsAppOnCreate : false}
                      onChange={(e) => handleChange('sendWhatsAppOnCreate', e.target.checked)}
                      color="success"
                      disabled={loading || !waSettings?.enabled}
                    />
                  }
                  label="Notificar o cliente por WhatsApp ao criar"
                />
                {!waSettings?.enabled && (
                  <Typography variant="caption" color="text.secondary" sx={{ display: 'block', ml: 6, mt: -0.5 }}>
                    A integração com WhatsApp está desativada.{' '}
                    <Link component="button" type="button" onClick={() => navigate('/integrations')} sx={{ verticalAlign: 'baseline' }}>
                      Ativar integração
                    </Link>
                  </Typography>
                )}
              </Box>

              {/* Confirmação de envio de WhatsApp na criação (mensagem tem custo — telefone em foco) */}
              <Dialog open={waConfirm.open} onClose={() => setWaConfirm({ open: false, personId: null })} maxWidth="xs" fullWidth>
                <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <WhatsAppIcon color="success" /> Confirmar envio de WhatsApp
                </DialogTitle>
                <DialogContent>
                  <Typography variant="body2" sx={{ mb: 2 }}>
                    Ao salvar, uma mensagem de WhatsApp será enviada ao cliente confirmando o
                    agendamento. <strong>O envio consome créditos</strong> — confira se o telefone
                    está correto:
                  </Typography>
                  <Box sx={{ p: 1.5, mb: 2, bgcolor: '#f5f5f5', borderRadius: 1, textAlign: 'center' }}>
                    <Typography variant="h6" fontWeight="bold">
                      {maskPhoneBR(formData.clientPhone)}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">{formData.clientName}</Typography>
                  </Box>
                  <Typography variant="body2" color="text.secondary">
                    {[
                      services.find((s) => s.id === formData.serviceId)?.name,
                      professionals.find((p) => p.id === formData.professionalId)?.name,
                      formData.customTime
                        ? (formData.customStart ? new Date(formData.customStart).toLocaleString('pt-BR', { weekday: 'short', day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit' }) : null)
                        : (formData.selectedSlot ? formatSlotDateTime(formData.selectedSlot) : null),
                    ].filter(Boolean).join(' · ')}
                  </Typography>
                </DialogContent>
                <DialogActions>
                  <Button onClick={() => setWaConfirm({ open: false, personId: null })} disabled={loading}>
                    Voltar
                  </Button>
                  <Button
                    variant="contained"
                    color="success"
                    startIcon={<WhatsAppIcon />}
                    disabled={loading}
                    onClick={async () => {
                      const personId = waConfirm.personId;
                      setWaConfirm({ open: false, personId: null });
                      await doSubmit(personId, { waConfirmed: true });
                    }}
                  >
                    Salvar e enviar
                  </Button>
                </DialogActions>
              </Dialog>

              {/* Dialog de confirmação de pessoa existente */}
              <Dialog open={confirmDialog.open} onClose={() => setConfirmDialog({ open: false, matches: [] })} maxWidth="sm" fullWidth>
                <DialogTitle>Pessoa com dados semelhantes encontrada</DialogTitle>
                <DialogContent>
                  <Typography variant="body2" sx={{ mb: 2 }}>
                    Existem pessoas cadastradas com telefone ou e-mail semelhantes. Deseja usar uma delas ou criar uma nova pessoa?
                  </Typography>
                  {confirmDialog.matches.map(person => (
                    <Box
                      key={person.id}
                      sx={{
                        p: 1.5, mb: 1, bgcolor: '#f5f5f5', borderRadius: 1,
                        cursor: 'pointer', '&:hover': { bgcolor: '#e0e0e0' },
                        display: 'flex', justifyContent: 'space-between', alignItems: 'center'
                      }}
                      onClick={async () => {
                        setConfirmDialog({ open: false, matches: [] });
                        setSelectedPerson(person);
                        await doSubmit(person.id);
                      }}
                    >
                      <Box>
                        <Typography variant="body2" fontWeight="bold">{person.name}</Typography>
                        <Typography variant="caption" color="text.secondary">
                          {person.phone ? maskPhoneBR(person.phone) : ''}{person.email ? ` · ${person.email}` : ''}
                        </Typography>
                      </Box>
                      <Chip label="Usar esta" size="small" color="primary" />
                    </Box>
                  ))}
                </DialogContent>
                <DialogActions>
                  <Button onClick={() => setConfirmDialog({ open: false, matches: [] })}>
                    Cancelar
                  </Button>
                  <Button
                    variant="contained"
                    onClick={async () => {
                      setConfirmDialog({ open: false, matches: [] });
                      await doSubmit();
                    }}
                  >
                    Criar nova pessoa
                  </Button>
                </DialogActions>
              </Dialog>
            </>
          )}
        </Box>
          </>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={handleClose} disabled={loading || loadingData}>
          Cancelar
        </Button>
        <Tooltip
          title={submitButtonDisabledReason || (isEdit ? 'Salvar alterações' : 'Criar novo agendamento')}
          placement="top"
          disableHoverListener={!submitButtonDisabledReason}
        >
          <span>
            <Button
              onClick={handleSubmit}
              variant="contained"
              disabled={!canSubmit || loadingData}
            >
              {loading ? <CircularProgress size={24} /> : (isEdit ? 'Salvar Alterações' : 'Criar Agendamento')}
            </Button>
          </span>
        </Tooltip>
      </DialogActions>

      {/* Services Manager Dialog */}
      <ServicesManagerDialog
        open={servicesManagerOpen}
        onClose={handleServicesManagerClose}
      />
    </Dialog>
  );
}
